import React, { useEffect, useState } from "react";
import { PlayCircle, DeviceMobile, DownloadSimple } from "@phosphor-icons/react";
import InlineShareButton from "./InlineShareButton";
import { shareOrigin } from "../lib/shareOrigin";

const LANGS = [
  { key: "en", label: "English", suffix: "", track: "English" },
  { key: "es", label: "Español", suffix: "_es", track: "Español" },
];

const CLIPS = [
  { file: "clip_smoke_checks.mp4", label: "Smoke checks · 53s" },
  { file: "clip_fires.mp4", label: "Active fires · 46s" },
  { file: "clip_notifications.mp4", label: "Notifications · 71s" },
  { file: "firewatch_tutorial_mobile_captioned.mp4", label: "Phone tour · 2 min" },
];

function Player({ name, lang, poster, className, testid }) {
  const l = LANGS.find((x) => x.key === lang);
  return (
    <video
      key={`${name}${l.suffix}`}
      data-testid={testid}
      className={className}
      controls
      playsInline
      preload="metadata"
      poster={poster}
    >
      <source src={`/docs/${name}${l.suffix}.mp4`} type="video/mp4" />
      <track kind="captions" srcLang={lang} label={l.track} src={`/docs/${name}${l.suffix}.vtt`} default />
    </video>
  );
}

function LangToggle({ lang, setLang }) {
  return (
    <div className="flex items-center border border-tac-border rounded-sm overflow-hidden" data-testid="guide-video-lang">
      {LANGS.map((l) => (
        <button
          key={l.key}
          data-testid={`guide-video-lang-${l.key}`}
          onClick={() => setLang(l.key)}
          className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${
            lang === l.key ? "bg-tac-primary text-black" : "text-tac-dim hover:text-tac-text"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

function ShareRow({ testid, url, caption, title }) {
  return (
    <InlineShareButton
      testid={testid}
      url={url}
      title={title}
      text={caption}
      caption={caption}
    />
  );
}

export default function GuideVideos() {
  const [meta, setMeta] = useState(null);
  const [lang, setLang] = useState(() => (navigator.language || "").toLowerCase().startsWith("es") ? "es" : "en");
  useEffect(() => {
    fetch("/docs/tutorial_meta.json").then((r) => (r.ok ? r.json() : null)).then(setMeta).catch(() => {});
  }, []);

  const guideUrl = `${shareOrigin()}/guide`;
  const desktopCaption = [
    "🎬 How to use Firewatch — 5-minute walkthrough (English + Español)",
    "Smoke checks, active fires, and every alert the app can send you: new fires near your pin, satellite hotspots, evacuation levels, rapid growth, lightning, flood and debris-flow warnings, daily briefings.",
    `Watch it here: ${guideUrl}`,
  ].join("\n");
  const mobileCaption = [
    "📱 Firewatch on your phone — 2-minute tour",
    "Map / Feed switch, the Fires · DOT · More tabs, and how to add Firewatch to your home screen on iPhone and Android so it opens like an app and sends push alerts.",
    `Watch it here: ${guideUrl}`,
  ].join("\n");

  return (
    <section
      className="border border-tac-primary/40 bg-tac-surface/40 rounded-sm mb-8 overflow-hidden"
      data-testid="guide-video-section"
    >
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-tac-border">
        <PlayCircle size={20} weight="fill" className="text-tac-primary" />
        <div className="min-w-0 flex-1">
          <h2 className="font-display font-black text-lg tracking-tight text-tac-text">
            Watch the 5-minute walkthrough
          </h2>
          <p className="text-[13px] text-tac-dim">
            Smoke checks, fires, and every notification Firewatch can send you. Captions included.
          </p>
        </div>
        <LangToggle lang={lang} setLang={setLang} />
        {meta?.recorded_at && (
          <span className="shrink-0 text-[11px] font-mono-tac uppercase tracking-wider text-tac-faint" data-testid="guide-video-recorded">
            Recorded {meta.recorded_at}
          </span>
        )}
      </div>
      <Player
        name="firewatch_tutorial"
        lang={lang}
        poster="/docs/firewatch_tutorial_poster.jpg"
        className="w-full aspect-video bg-black"
        testid="guide-tutorial-video"
      />
      <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-t border-tac-border" data-testid="guide-video-share-row">
        <ShareRow testid="guide-video-share" url={guideUrl} title="How to use Firewatch" caption={desktopCaption} />
        <span className="text-[11px] uppercase tracking-wider text-tac-faint ml-1">Download for Facebook:</span>
        {CLIPS.map((c) => (
          <a
            key={c.file}
            href={`/docs/${c.file}`}
            download
            data-testid={`guide-clip-${c.file.replace(/\..*$/, "")}`}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold border border-tac-border bg-tac-bg text-tac-text rounded-sm hover:border-tac-primary transition-colors"
          >
            <DownloadSimple size={12} weight="bold" /> {c.label}
          </a>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-4 px-5 py-4 border-t border-tac-border" data-testid="guide-mobile-video-section">
        <Player
          name="firewatch_tutorial_mobile"
          lang={lang}
          poster="/docs/firewatch_tutorial_mobile_poster.jpg"
          className="w-full sm:w-[200px] shrink-0 aspect-[9/19.5] bg-black rounded-sm border border-tac-border"
          testid="guide-mobile-video"
        />
        <div className="min-w-0 flex flex-col">
          <div className="flex items-center gap-2 mb-1">
            <DeviceMobile size={16} weight="fill" className="text-tac-primary" />
            <h3 className="font-display font-black text-base text-tac-text">On your phone</h3>
          </div>
          <p className="text-[14px] text-tac-dim leading-relaxed">
            A two-minute phone-size tour: the Map / Feed switch, the Fires · DOT · More tabs,
            and how to install Firewatch on your home screen on iPhone and Android so it opens
            like a real app and receives push alerts.
          </p>
          <div className="mt-3">
            <ShareRow testid="guide-mobile-video-share" url={guideUrl} title="Firewatch on your phone" caption={mobileCaption} />
          </div>
        </div>
      </div>
    </section>
  );
}
